import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Box, Avatar, IconButton, Divider, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LayoutDashboard, Building2, Settings, BotMessageSquare, Upload, History, User, LogOut, Sun, Moon, Pin, PinOff, Search } from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { useSignOut, useFunds, useTenant, useRealtimeFunds } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { agentStyles } from '../../theme/theme';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { GlobalSearchDialog } from '../GlobalSearchDialog';
import { UpdateNotificationButton } from '../UpdateNotificationButton';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const ICON_SIZE = ICON_SIZES.lg;

// Navigation items by role
const navigationByRole: Record<string, NavItem[]> = {
  super_admin: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Tenants',
      path: '/tenants',
      icon: <Building2 size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: <Settings size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
  ],
  admin: [
    {
      label: 'Portfolio Operations Analyst Agent',
      path: '/dashboard',
      icon: <BotMessageSquare size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
  ],
  portco: [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Submit Metrics',
      path: '/submit',
      icon: <Upload size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'History',
      path: '/history',
      icon: <History size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
    {
      label: 'Profile',
      path: '/profile',
      icon: <User size={ICON_SIZE} strokeWidth={ICON_STROKE} />,
    },
  ],
};

export function Sidebar() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const signOut = useSignOut();
  const { data: funds } = useFunds(user?.tenant_id ?? undefined);
  const { data: tenant } = useTenant(user?.tenant_id ?? '');
  useRealtimeFunds(user?.tenant_id ?? undefined);

  const { sidebarPinned, sidebarOpen, setSidebarPinned, setSidebarOpen, themeMode, toggleTheme } = useUIStore();

  // Agent status - TODO: Connect to actual agent state
  const [isAgentActive] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  const isExpanded = sidebarPinned || sidebarOpen;
  const navItems = user?.role ? navigationByRole[user.role] || [] : [];
  const isAdmin = user?.role === 'admin';
  const activeFundId = location.pathname.startsWith('/funds/') ? location.pathname.split('/')[2] : null;

  const handleSignOut = async () => {
    await signOut.mutateAsync();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isExpanded ? 300 : 72,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isExpanded ? 300 : 72,
          boxSizing: 'border-box',
          transition: 'width 200ms',
          overflowX: 'hidden',
        },
      }}
      onMouseEnter={() => !sidebarPinned && setSidebarOpen(true)}
      onMouseLeave={() => !sidebarPinned && setSidebarOpen(false)}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header with pin toggle */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isExpanded ? 'space-between' : 'center',
            minHeight: 64,
          }}
        >
          {isExpanded ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.svg" alt="Linga AI" style={{ height: '24px', width: 'auto' }} />
              <Typography variant="h6" noWrap>
                Linga AI
              </Typography>
            </Box>
          ) : (
            <img src="/logo.svg" alt="Linga AI" style={{ height: '24px', width: 'auto' }} />
          )}
          {isExpanded && (
            <Tooltip title={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'} placement="right">
              <IconButton size="small" onClick={() => setSidebarPinned(!sidebarPinned)}>
                {sidebarPinned ? <PinOff size={18} strokeWidth={1} /> : <Pin size={18} strokeWidth={1} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        {/* Agent Status Indicator */}
        {isAgentActive && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Avatar sx={agentStyles.agentAvatar(theme.palette.mode)}>DA</Avatar>
              {isExpanded && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">Data Analyst</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: agentStyles.statusColors.processing,
                        ...agentStyles.agentPulse,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Processing
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Navigation List */}
        <List sx={{ pt: 1 }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                <Tooltip title={!isExpanded ? item.label : ''} placement="right">
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      borderRadius: 1,
                      minHeight: 48,
                      justifyContent: isExpanded ? 'initial' : 'center',
                      px: 2.5,
                      bgcolor: isActive ? 'primary.main' : 'transparent',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        bgcolor: isActive ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isExpanded ? 2 : 'auto',
                        justifyContent: 'center',
                        color: isActive ? 'primary.contrastText' : 'inherit',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {isExpanded && <ListItemText primary={item.label} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {/* Funds Section - Admin Only */}
        {isAdmin && funds && funds.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1 }}>
              {isExpanded && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                  FUNDS
                </Typography>
              )}
            </Box>
            <List sx={{ pt: 0 }}>
              {funds.map((fund) => {
                const isActive = activeFundId === fund.id;
                // Use fund abbreviation from database, fallback to first 3 letters of name
                const abbrev = fund.abbrev || fund.name.substring(0, 3).toUpperCase();

                return (
                  <ListItem key={fund.id} disablePadding sx={{ px: 1 }}>
                    <Tooltip title={!isExpanded ? fund.name : ''} placement="right">
                      <ListItemButton
                        onClick={() => navigate(`/funds/${fund.id}`)}
                        sx={{
                          borderRadius: 1,
                          minHeight: 40,
                          justifyContent: isExpanded ? 'initial' : 'center',
                          px: 2.5,
                          bgcolor: isExpanded && isActive ? 'action.selected' : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: isExpanded ? 2 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              height: 24,
                              minWidth: isExpanded ? 64 : 'auto',
                              px: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 3,
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              bgcolor: isActive ? 'primary.main' : 'action.hover',
                              color: isActive ? 'primary.contrastText' : 'text.secondary',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {abbrev}
                          </Box>
                        </ListItemIcon>
                        {isExpanded && <ListItemText primary={fund.name} primaryTypographyProps={{ variant: 'body2' }} />}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Divider />

        {/* Update Notification */}
        <UpdateNotificationButton />

        {/* User Context Section */}
        <Box sx={{ p: 2 }}>
          {/* User Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1.5,
              justifyContent: isExpanded ? 'flex-start' : 'center',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {getUserInitials()}
            </Avatar>
            {isExpanded && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {user?.full_name || user?.email}
                </Typography>
                {user?.role !== 'super_admin' && tenant?.name && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {tenant.name}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: isExpanded ? 'flex-start' : 'center',
              flexDirection: isExpanded ? 'row' : 'column',
            }}
          >
            {/* Search - Admin Only */}
            {isAdmin && (
              <Tooltip title="Search" placement="right">
                <IconButton onClick={() => setSearchDialogOpen(true)} size="small">
                  <Search size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
                </IconButton>
              </Tooltip>
            )}

            {/* Settings */}
            <Tooltip title="Settings" placement="right">
              <IconButton onClick={() => navigate('/settings')} size="small">
                <Settings size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
              </IconButton>
            </Tooltip>

            {/* Theme Toggle */}
            <Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`} placement="right">
              <IconButton onClick={toggleTheme} size="small">
                {themeMode === 'light' ? <Moon size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Sun size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
              </IconButton>
            </Tooltip>

            {/* Logout */}
            <Tooltip title="Sign out" placement="right">
              <IconButton onClick={handleSignOut} size="small" disabled={signOut.isPending}>
                <LogOut size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Global Search Dialog */}
      {isAdmin && user?.tenant_id && <GlobalSearchDialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} tenantId={user.tenant_id} />}
    </Drawer>
  );
}
